import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { formatUtcTimestamp } from "_shared/formatting/dates";
import {
  useGetNotificationEmailReminderOperationsQuery,
  useSetNotificationEmailReminderSwitchMutation,
} from "notifications/email-reminders/_api/notification-email-reminder-operations-api";
import type {
  NotificationEmailReminderActivityJSON,
  NotificationEmailReminderOperationsJSON,
} from "notifications/email-reminders/_schemas/notification-email-reminder-operations-schema";
import React, { useState, type ReactNode } from "react";

/** How often the tab reads again while it is open, so a pause or a growing backlog shows up. */
export const OPERATIONS_POLLING_INTERVAL_MS = 30_000;
const REASON_MAX_LENGTH = 500;

const Page = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));
const Figures = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))",
}));
const Figure = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));
const Section = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

/** In UTC, labelled: the cutoff, check windows and the server's logs are all UTC. */
function formatInstant(value: string | null | undefined): ReactNode {
  return formatUtcTimestamp(value) ?? <NoValueDash />;
}

/**
 * The super user tab for unread-notification reminder emails: what configuration allows, whether a
 * super user has paused sending, the backlog, recent emails, and who paused or resumed and why.
 */
export default function EmailReminderOperations() {
  const operations = useGetNotificationEmailReminderOperationsQuery(undefined, {
    pollingInterval: OPERATIONS_POLLING_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });
  const [setSwitch, switchState] =
    useSetNotificationEmailReminderSwitchMutation();
  const [pausing, setPausing] = useState(false);
  const [reason, setReason] = useState("");

  if (operations.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress aria-label="Loading email reminders" />
      </Box>
    );
  }
  const retry = (
    <Button
      color="inherit"
      size="small"
      disabled={operations.isFetching}
      onClick={() => operations.refetch()}
    >
      Try again
    </Button>
  );
  if (!operations.data) {
    return (
      <Alert severity="error" sx={{ marginTop: 2 }} action={retry}>
        The email reminder status could not be loaded.
      </Alert>
    );
  }

  const data = operations.data;
  const trimmedReason = reason.trim();

  async function pause() {
    const result = await setSwitch({ paused: true, reason: trimmedReason });
    if ("error" in result) return;
    setPausing(false);
    setReason("");
  }

  return (
    <Page>
      {operations.isError && (
        // A failed poll keeps the last answer, so say the figures below may no longer be true.
        <Alert severity="error" action={retry}>
          The email reminder status could not be refreshed. What follows was
          read at{" "}
          {formatInstant(
            operations.fulfilledTimeStamp === undefined
              ? null
              : new Date(operations.fulfilledTimeStamp).toISOString(),
          )}{" "}
          and may be out of date.
        </Alert>
      )}
      <StatusBanner data={data} />
      {switchState.isError && (
        <Alert severity="error">
          The change could not be saved. Read the status above and try again.
        </Alert>
      )}
      <Box>
        {data.switch.isPaused ? (
          <Button
            id="email-reminders-resume-button"
            variant="contained"
            disabled={switchState.isLoading}
            onClick={() => setSwitch({ paused: false })}
          >
            Resume reminder emails
          </Button>
        ) : (
          <Button
            id="email-reminders-pause-button"
            variant="contained"
            color="warning"
            disabled={switchState.isLoading}
            onClick={() => setPausing(true)}
          >
            Pause reminder emails
          </Button>
        )}
      </Box>

      <Section aria-labelledby="email-reminders-backlog-heading">
        <Typography id="email-reminders-backlog-heading" variant="h6">
          Backlog
        </Typography>
        <Figures>
          <FigureCard
            label="Reminders waiting"
            value={data.backlog.pendingCount}
          />
          <FigureCard
            label="Oldest due"
            value={formatInstant(data.backlog.oldestDueAtUtc)}
          />
          <FigureCard
            label="Emails in progress"
            value={data.backlog.activeBatchCount}
          />
          <FigureCard
            label="Expired claims"
            value={data.backlog.expiredLeaseCount}
          />
        </Figures>
      </Section>

      <Section aria-labelledby="email-reminders-activity-heading">
        <Typography id="email-reminders-activity-heading" variant="h6">
          Emails
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="right">Sent</TableCell>
                <TableCell align="right">Cancelled</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell align="right">Retrying now</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <ActivityRow label="Last 24 hours" activity={data.last24Hours} />
              <ActivityRow label="Last 7 days" activity={data.last7Days} />
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section aria-labelledby="email-reminders-recent-heading">
        <Typography id="email-reminders-recent-heading" variant="h6">
          Recent emails
        </Typography>
        {data.recentBatches.length === 0 ? (
          <Typography color="text.secondary">
            No reminder emails yet.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Window</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell>Failure</TableCell>
                  <TableCell>Finished</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentBatches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>{formatInstant(batch.checkWindowUtc)}</TableCell>
                    <TableCell>
                      {batch.recipient ?? "Account removed"}
                    </TableCell>
                    <TableCell>{batch.status}</TableCell>
                    <TableCell align="right">{batch.attemptCount}</TableCell>
                    <TableCell>{batch.failureCode ?? <NoValueDash />}</TableCell>
                    <TableCell>{formatInstant(batch.completedAtUtc)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section aria-labelledby="email-reminders-history-heading">
        <Typography id="email-reminders-history-heading" variant="h6">
          Pause history
        </Typography>
        {data.recentChanges.length === 0 ? (
          <Typography color="text.secondary">
            Reminder emails have never been paused.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Skipped on resume</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentChanges.map((change) => (
                  <TableRow key={`${change.changedAtUtc}-${change.paused}`}>
                    <TableCell>{formatInstant(change.changedAtUtc)}</TableCell>
                    <TableCell>
                      {change.paused ? "Paused" : "Resumed"}
                    </TableCell>
                    <TableCell>{change.changedBy ?? <NoValueDash />}</TableCell>
                    <TableCell>{change.reason ?? <NoValueDash />}</TableCell>
                    <TableCell align="right">
                      {change.skippedReminderCount ?? <NoValueDash />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section aria-labelledby="email-reminders-configuration-heading">
        <Typography id="email-reminders-configuration-heading" variant="h6">
          Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Read-only. These change through Terraform and take effect when the API
          restarts.
        </Typography>
        <Figures>
          <FigureCard
            label="Environment"
            value={data.configuration.environment}
          />
          <FigureCard
            label="Enabled"
            value={data.configuration.enabled ? "Yes" : "No"}
          />
          <FigureCard
            label="Activation cutoff"
            value={formatInstant(data.configuration.activationCutoffUtc)}
          />
          <FigureCard
            label="Unread for"
            value={`${data.configuration.defaultUnreadThresholdMinutes} min`}
          />
          <FigureCard
            label="Checks every"
            value={`${data.configuration.pollIntervalSeconds} s`}
          />
        </Figures>
      </Section>

      <Dialog
        open={pausing}
        onClose={() => setPausing(false)}
        aria-labelledby="email-reminders-pause-title"
      >
        <DialogTitle id="email-reminders-pause-title">
          Pause reminder emails
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            No further email starts once a worker sees the pause: straight away
            while it is sending, and within about{" "}
            {data.configuration.pollIntervalSeconds} seconds while it is idle.
            An email already being sent finishes. When you resume, reminders
            that came due while paused are skipped, not sent.
          </DialogContentText>
          <TextField
            id="email-reminders-pause-reason"
            label="Reason"
            required
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
            helperText="Shown to other super users in the pause history."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPausing(false)}>Cancel</Button>
          <Button
            id="email-reminders-confirm-pause-button"
            color="warning"
            variant="contained"
            disabled={trimmedReason.length === 0 || switchState.isLoading}
            onClick={pause}
          >
            Pause
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

function StatusBanner(
  props: Readonly<{ data: NotificationEmailReminderOperationsJSON }>,
) {
  const { data } = props;
  if (data.effectiveState === "OffInConfiguration") {
    return (
      <Alert severity="info" id="email-reminders-status">
        Reminder emails are off in this environment&apos;s configuration.
        {data.switch.isPaused &&
          " They are also paused, so they stay paused when configuration turns them on."}
      </Alert>
    );
  }
  if (data.effectiveState === "Paused") {
    return (
      <Alert severity="warning" id="email-reminders-status">
        Reminder emails are paused
        {data.switch.changedBy ? ` by ${data.switch.changedBy}` : ""} since{" "}
        {formatInstant(data.switch.pausedAtUtc)}: {data.switch.reason}
      </Alert>
    );
  }
  return (
    <Alert severity="success" id="email-reminders-status">
      Reminder emails are being sent.
    </Alert>
  );
}

function FigureCard(
  props: Readonly<{ label: string; value: React.ReactNode }>,
) {
  return (
    <Figure variant="outlined">
      <Typography variant="body2" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="h6">{props.value}</Typography>
    </Figure>
  );
}

function ActivityRow(
  props: Readonly<{
    label: string;
    activity: NotificationEmailReminderActivityJSON;
  }>,
) {
  const { activity } = props;
  return (
    <TableRow>
      <TableCell>{props.label}</TableCell>
      <TableCell align="right">{activity.sentBatchCount}</TableCell>
      <TableCell align="right">{activity.cancelledBatchCount}</TableCell>
      <TableCell align="right">{activity.failedBatchCount}</TableCell>
      <TableCell align="right">{activity.retryingBatchCount}</TableCell>
    </TableRow>
  );
}
